import { Lead } from 'types/Apis/Lead';
import { Badge } from 'components/VehicleBadges/types';
import { differenceInMilliseconds } from 'date-fns';
import { FavoriteDataItem, SearchVehicle } from 'types/Apis/Favorites';
import { getFavoritesBadges } from './BadgeDisplayLogic';

const ACCEPTING_OFFERS = 'Accepting offers';
const FRESHNESS_THRESHOLD = 86400000; // 24 hours in ms

export interface FavoriteModel {
  id: number;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: string;
  price: string;
  city: string;
  state: string;
  vdpUrl: string;
  thumbnailImg: string;
  active: boolean;
  badges: Badge[];
  hasPriceDroppedSinceFavoriting: boolean;
  isAcceptingOffers: boolean;
}

function isRecent(createdAt: string): boolean {
  return differenceInMilliseconds(new Date(), Date.parse(createdAt)) < FRESHNESS_THRESHOLD;
}

class FavoritePresenter implements FavoriteModel {
  private favoriteData: FavoriteDataItem;

  private searchVehicle: SearchVehicle;

  contactedFavorite?: Lead;

  constructor(favoriteData: FavoriteDataItem, contactedFavorite?: Lead) {
    this.favoriteData = favoriteData;
    this.searchVehicle = favoriteData.search_vehicle;
    this.contactedFavorite = contactedFavorite;
  }

  get id(): number {
    return this.favoriteData.id;
  }

  get vin(): string {
    return this.favoriteData.vin;
  }

  get make(): string {
    return this.favoriteData.make;
  }

  get model(): string {
    return this.favoriteData.model;
  }

  get year(): number {
    return this.favoriteData.year;
  }

  get trim(): string {
    return this.searchVehicle.trim;
  }

  get mileage(): string {
    if (this.searchVehicle.mileage === 'New') {
      return this.searchVehicle.mileage;
    }
    return this.searchVehicle.mileage.toLowerCase();
  }

  get price(): string {
    if (this.searchVehicle.price === 'accepting_offers') {
      return ACCEPTING_OFFERS;
    }
    return this.searchVehicle.price;
  }

  get city(): string {
    return this.searchVehicle.city;
  }

  get state(): string {
    return this.searchVehicle.state;
  }

  get vdpUrl(): string {
    return this.searchVehicle.vdp_url;
  }

  get thumbnailImg(): string {
    return this.searchVehicle.thumbnail_url_large;
  }

  get active(): boolean {
    return this.searchVehicle.active;
  }

  get badges(): Badge[] {
    return getFavoritesBadges(this);
  }

  get vehicleWasRecentlyListed(): boolean {
    return isRecent(this.searchVehicle.created_at);
  }

  get vehicleHasRecentPriceDrop(): boolean {
    return this.favoriteData.recent_price_drop || false;
  }

  get vehicleIsHot(): boolean {
    const isHot = this.searchVehicle.is_hot;
    return isHot || false;
  }

  get userHasRecentlyContacted(): boolean {
    if (this.contactedFavorite) {
      return isRecent(this.contactedFavorite.created_at);
    }

    return false;
  }

  get hasPriceDroppedSinceFavoriting(): boolean {
    if (!this.favoriteData.price_dropped_at) {
      return false;
    }

    const createdAtDate = new Date(this.favoriteData.created_at);
    const priceDroppedDate = new Date(this.favoriteData.price_dropped_at);
    return createdAtDate.getTime() < priceDroppedDate.getTime();
  }

  get isAcceptingOffers(): boolean {
    return this.searchVehicle.price === 'accepting_offers';
  }

  toJSON(): FavoriteModel {
    return {
      id: this.id,
      vin: this.vin,
      make: this.make,
      model: this.model,
      trim: this.trim,
      year: this.year,
      mileage: this.mileage,
      price: this.price,
      city: this.city,
      state: this.state,
      vdpUrl: this.vdpUrl,
      thumbnailImg: this.thumbnailImg,
      active: this.active,
      badges: this.badges,
      hasPriceDroppedSinceFavoriting: this.hasPriceDroppedSinceFavoriting,
      isAcceptingOffers: this.isAcceptingOffers,
    };
  }
}

export default FavoritePresenter;
